import React, { useState } from 'react';
import { Send, CheckCircle, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import './FormContact.css';
import { useFormsSwitchedOff, PreviewFormNotice } from '../components/PreviewFormNotice';

const FormContact = () => {
  const [submitting, setSubmitting] = useState(false);
  const formsOff = useFormsSwitchedOff();
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    message: '',
    bestTimeToContact: '',
    contactPreference: 'email',
    reasonForContact: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formsOff) return;
    setSubmitting(true);
    setSubmitError('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      // Create FormData object (backend expects form data, not JSON)
      const formDataToSend = new FormData();
      formDataToSend.append('first_name', formData.firstName);
      formDataToSend.append('last_name', formData.lastName);
      formDataToSend.append('phone_number', formData.phoneNumber);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('best_time_to_contact', formData.bestTimeToContact);
      formDataToSend.append('contact_preference', formData.contactPreference);
      formDataToSend.append('reason_for_contact', formData.reasonForContact);

      const response = await fetch(`${backendUrl}/api/contact/submit`, {
        method: 'POST',
        body: formDataToSend
        // Note: Don't set Content-Type header - browser will set it automatically with boundary
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitSuccess(true);
        // Reset form after 5 seconds
        setTimeout(() => {
          setSubmitSuccess(false);
          setFormData({
            firstName: '',
            lastName: '',
            phoneNumber: '',
            email: '',
            message: '',
            bestTimeToContact: '',
            contactPreference: 'email',
            reasonForContact: ''
          });
        }, 5000);
      } else {
        // Handle error message - could be string, array, or object
        let errorMsg = 'Submission failed. Please try again.';
        if (typeof data.detail === 'string') {
          errorMsg = data.detail;
        } else if (Array.isArray(data.detail)) {
          errorMsg = data.detail.map(err => err.msg || err).join(', ');
        } else if (data.message) {
          errorMsg = data.message;
        }
        setSubmitError(errorMsg);
      }
    } catch (error) {
      setSubmitError('Network error. Please check your connection and try again.');
      console.error('Contact form error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="form-contact-standalone">
      <Link to="/" className="form-back-home">
        <Home size={20} />
        <span>Back to Home</span>
      </Link>
      <div className="form-container">
        <div className="form-header">
          <div className="form-branding">
            <img src="/logo.png" alt="Clinical Minds Psychology" className="form-logo" />
          </div>
          <h1 className="form-title">Contact Us</h1>
          <p className="form-subtitle">Send us a message and we'll get back to you within 24 hours</p>
        </div>

        <div className="form-content">
          {submitSuccess ? (
            <div className="success-message">
              <CheckCircle size={48} className="success-icon" />
              <h3>Message Sent Successfully!</h3>
              <p>Thank you for contacting us. We'll get back to you within 24 hours.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="contact-form">
              {formsOff && <PreviewFormNotice />}
              <div className="privacy-notice">
                <p>We take your privacy seriously. All data and files you submit are securely stored in Australia in compliance with The Privacy Act (1988) and the Australian Privacy Principles (APPs). You can find full details in our <a href="https://www.clinicalminds.net/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</p>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phoneNumber">Phone Number</label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="contactPreference">Preferred Contact Method *</label>
                <select
                  id="contactPreference"
                  name="contactPreference"
                  value={formData.contactPreference}
                  onChange={handleInputChange}
                  required
                  disabled={submitting}
                >
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="sms">SMS</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="bestTimeToContact">Best Time to Contact</label>
                <input
                  type="text"
                  id="bestTimeToContact"
                  name="bestTimeToContact"
                  value={formData.bestTimeToContact}
                  onChange={handleInputChange}
                  placeholder="e.g., Weekday mornings, After 6 PM"
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="reasonForContact">What is this enquiry about?</label>
                <select
                  id="reasonForContact"
                  name="reasonForContact"
                  value={formData.reasonForContact}
                  onChange={handleInputChange}
                  disabled={submitting}
                  data-testid="reason-for-contact-select"
                >
                  <option value="">-- Please select --</option>
                  <option value="Autism assessment">Autism assessment</option>
                  <option value="ADHD assessment">ADHD assessment</option>
                  <option value="FASD assessment">FASD assessment</option>
                  <option value="Learning assessment">Learning assessment</option>
                  <option value="Therapy">Therapy</option>
                  <option value="School partnership">School partnership</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows="5"
                  required
                  disabled={submitting}
                  placeholder="Please tell us how we can help you..."
                ></textarea>
              </div>

              {submitError && (
                <div className="error-message">
                  <p>{submitError}</p>
                </div>
              )}

              <div className="form-footer">
                <p className="consent-text">
                  By submitting this form, you confirm that you have read and agree to our <a href="https://www.clinicalminds.net/privacy-policy" target="_blank" rel="noopener noreferrer">privacy policy</a>.
                </p>
                <button 
                  type="submit" 
                  className="btn-submit"
                  disabled={submitting || formsOff}
                >
                  {submitting ? (
                    <>
                      <span className="spinner"></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Send Message
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormContact;